import { useState, useCallback } from "react";
import { checkGeo } from "@/lib/api";

export interface UseGeofenceReturn {
  checking: boolean;
  verified: boolean;
  error: string | null;
  distanceMeters: number | null;
  checkLocation: (eventSlug: string, token: string) => Promise<boolean>;
}

export function useGeofence(): UseGeofenceReturn {
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distanceMeters, setDistanceMeters] = useState<number | null>(null);

  const checkLocation = useCallback(
    async (eventSlug: string, token: string): Promise<boolean> => {
      setChecking(true);
      setError(null);
      setDistanceMeters(null);

      try {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 15_000,
              maximumAge: 0,
            });
          },
        );

        const { latitude, longitude } = position.coords;

        const result = await checkGeo(eventSlug, token, latitude, longitude);

        setDistanceMeters(result.distanceMeters);

        if (result.allowed) {
          setVerified(true);
          setChecking(false);
          return true;
        }

        setError(
          `You are ${formatDistance(result.distanceMeters)} away from the event. Please move closer and try again.`,
        );
        setChecking(false);
        return false;
      } catch (err) {
        if (err instanceof GeolocationPositionError) {
          switch (err.code) {
            case err.PERMISSION_DENIED:
              setError(
                "Location access was denied. Please enable location permissions and try again.",
              );
              break;
            case err.POSITION_UNAVAILABLE:
              setError(
                "Unable to determine your location. Please try again.",
              );
              break;
            case err.TIMEOUT:
              setError(
                "Location request timed out. Please try again in a moment.",
              );
              break;
            default:
              setError("Location check failed. Please try again.");
          }
        } else {
          setError(
            err instanceof Error
              ? err.message
              : "Location check failed. Please try again.",
          );
        }
        setChecking(false);
        return false;
      }
    },
    [],
  );

  return { checking, verified, error, distanceMeters, checkLocation };
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}
